import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../api/axiosInstance';

type AmazonMusicData = {
  title?: string;
  thumbnail?: string;
  description?: string;
  url?: string;
  artist?: string;
  playlistName?: string;
};

const amazonMusicCache = new Map<string, AmazonMusicData>();
const amazonMusicInflight = new Map<string, Promise<AmazonMusicData>>();

async function fetchAmazonMusic(url: string): Promise<AmazonMusicData> {
  const cached = amazonMusicCache.get(url);
  if (cached) return cached;

  const inflight = amazonMusicInflight.get(url);
  if (inflight) return inflight;

  const p = axiosInstance
    .get('/api/oembed', { params: { url } })
    .then(res => {
      const data = (res?.data ?? {}) as AmazonMusicData;
      // Ensure URL is set
      if (!data.url) {
        data.url = url;
      }
      return data;
    })
    .catch((error) => {
      console.error('Failed to fetch Amazon Music metadata:', error);
      return { url } as AmazonMusicData;
    })
    .finally(() => {
      amazonMusicInflight.delete(url);
    });

  amazonMusicInflight.set(url, p);
  const data = await p;
  amazonMusicCache.set(url, data);
  return data;
}

interface AmazonMusicPreviewProps {
  url: string;
}

export const AmazonMusicPreview: React.FC<AmazonMusicPreviewProps> = ({ url }) => {
  const [data, setData] = useState<AmazonMusicData>(() => amazonMusicCache.get(url) || { url });
  const [loading, setLoading] = useState<boolean>(() => !amazonMusicCache.has(url));

  useEffect(() => {
    let active = true;
    setLoading(!amazonMusicCache.has(url));

    fetchAmazonMusic(url).then(d => {
      if (!active) return;
      setData(d);
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [url]);

  const title = data.title || data.playlistName || 'Amazon Music';
  const thumbnail = data.thumbnail;
  const description = data.description || data.artist || '';
  
  // Ensure URL has play parameters for auto-play attempt
  const href = useMemo(() => {
    const baseUrl = data.url || url;
    try {
      const urlObj = new URL(baseUrl);
      // Ensure do=play parameter is present for auto-play
      if (!urlObj.searchParams.has('do')) {
        urlObj.searchParams.set('do', 'play');
      }
      // Ensure trackAsin is preserved if present in original URL
      const originalUrl = new URL(url);
      if (originalUrl.searchParams.has('trackAsin') && !urlObj.searchParams.has('trackAsin')) {
        urlObj.searchParams.set('trackAsin', originalUrl.searchParams.get('trackAsin')!);
      }
      return urlObj.toString();
    } catch {
      return baseUrl;
    }
  }, [data.url, url]);
  
  // Debug logging
  useEffect(() => {
    if (!loading && !thumbnail) {
      console.log('Amazon Music preview data:', data);
    }
  }, [data, loading, thumbnail]);
  
  // Check if we have any meaningful data beyond defaults
  const hasCustomTitle = data.title && data.title !== 'Amazon Music';
  const hasCustomDescription = data.description && 
    data.description !== 'Click to open in Amazon Music app' && 
    data.description.trim().length > 0;
  const hasData = hasCustomTitle || thumbnail || hasCustomDescription || data.artist;

  const domainLabel = useMemo(() => {
    try {
      const u = new URL(href);
      const hostname = u.hostname.replace(/^www\./i, '').replace(/^music\./i, '');
      // Extract domain (e.g., amazon.in, amazon.com)
      const parts = hostname.split('.');
      if (parts.length >= 2) {
        return parts.slice(-2).join('.').toUpperCase();
      }
      return hostname.toUpperCase();
    } catch {
      return 'AMAZON.IN';
    }
  }, [href]);

  // Extract a cleaner display title from URL if no custom title
  const displayTitle = useMemo(() => {
    if (hasCustomTitle) return title;
    
    // Don't try to extract from URL if it's just an ID (like B0F1N31W3D)
    // Only show "Amazon Music" as a simple, clean title
    return 'Amazon Music';
  }, [hasCustomTitle, title]);

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="block w-full max-w-xs overflow-hidden rounded-xl border border-blue-500/50 bg-[#0F1A23] hover:border-blue-400 transition-colors"
      onClick={(e) => e.stopPropagation()}
      title={href}
    >
      <div className="w-full relative">
        {thumbnail ? (
          <img 
            src={thumbnail} 
            alt={displayTitle} 
            className="w-full h-48 object-cover"
            onError={(e) => {
              // Hide image on error and show fallback
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : null}
        {!thumbnail && (
          <div className="w-full h-48 bg-gradient-to-br from-orange-600/40 to-orange-800/40 flex items-center justify-center">
            {loading ? (
              <div className="text-gray-300 text-sm">Loading preview…</div>
            ) : (
              <div className="text-center px-4">
                <div className="text-4xl mb-2">🎵</div>
                <div className="text-gray-300 text-sm font-medium">Amazon Music</div>
                {hasData && (
                  <div className="text-gray-400 text-xs mt-1">Tap to open</div>
                )}
              </div>
            )}
          </div>
        )}
        {/* Amazon Music logo overlay */}
        <div className="absolute top-2 left-2 bg-black/70 rounded px-2 py-1 backdrop-blur-sm">
          <span className="text-white text-xs font-semibold">amazon music</span>
        </div>
      </div>
      <div className="px-3 py-3">
        {hasCustomTitle ? (
          <>
            <div className="text-sm font-semibold text-gray-100 line-clamp-2 mb-1">{displayTitle}</div>
            {(description && hasCustomDescription) && (
              <div className="text-xs text-gray-300 line-clamp-2 mb-2">{description}</div>
            )}
            {data.artist && !hasCustomDescription && (
              <div className="text-xs text-gray-300 line-clamp-2 mb-2">by {data.artist}</div>
            )}
          </>
        ) : (
          <div className="text-sm font-medium text-gray-200 mb-2">Amazon Music</div>
        )}
        <div className="text-xs text-gray-400 flex items-center gap-2 mt-2">
          <span className="uppercase tracking-wide font-medium">{domainLabel}</span>
        </div>
        {/* Note: Auto-play may be restricted by browser/app policies */}
        {href.includes('do=play') && (
          <div className="text-xs text-gray-500 mt-1 italic">
            Note: You may need to click play after opening
          </div>
        )}
      </div>
    </a>
  );
};

